import type { BaseModalProps } from '@byecode/ui'
import { Empty, IconFont, MobileModal } from '@byecode/ui'
import React, { useCallback, useMemo, useState } from 'react'

import { useLanguageContext } from '../../../contexts'
import type { RoleOption } from '../../../types'
import * as SC from './styles'

interface RoleDrawerProps {
    opened: boolean
    isMultiple?: boolean
    leftIcon?: string
    options: RoleOption[]
    value: string[]
    title: string
    tagComponent?: React.FC<{ data: RoleOption }>
    target?: BaseModalProps['target']
    onChange?: (val: string[]) => void
    onClose: () => void
}

export const RoleDrawer: React.FC<RoleDrawerProps> = ({
    opened,
    leftIcon,
    isMultiple = false,
    options,
    tagComponent,
    title,
    value,
    target,
    onClose,
    onChange
}) => {
    const [roleValue, setRoleValue] = useState(value)
    const [searchVal, setSearchVal] = useState('')
    const { convertTextByLanguage } = useLanguageContext()
    const handleFinish = useCallback(() => {
        onChange?.(roleValue)
        onClose()
    }, [onChange, onClose, roleValue])

    const handleRoleVal = useCallback(
        (val: string) => {
            const cloneRoleValue = new Set(roleValue)
            const isSelected = cloneRoleValue.has(val)

            if (isSelected) {
                cloneRoleValue.delete(val)
                return [...cloneRoleValue]
            }
            cloneRoleValue.add(val)
            return [...cloneRoleValue]
        },
        [roleValue]
    )

    const handleRoleChange = useCallback(
        (val: string) => {
            if (!isMultiple) {
                onChange?.([val])
                setRoleValue([val])
                onClose()
                return
            }
            const newValue = handleRoleVal(val)
            setRoleValue(newValue)
        },
        [handleRoleVal, isMultiple, onChange, onClose]
    )

    const list = useMemo(() => options.filter(item => item.name.includes(searchVal)), [options, searchVal])

    return (
        <MobileModal
            leftSlot={!!leftIcon && <IconFont type={leftIcon} />}
            withCloseIcon={!isMultiple}
            data-ignore-click-away
            target={target}
            title={title}
            open={opened}
            styles={{
                modal: {
                    height: '50%'
                }
            }}
            onClose={onClose}
            extra={
                isMultiple ? (
                    <SC.FinishButton role="button" onClick={handleFinish}>
                        {convertTextByLanguage('complete')}
                    </SC.FinishButton>
                ) : null
            }
        >
            <SC.Container>
                <SC.SearchInput
                    value={searchVal}
                    placeholder={convertTextByLanguage('search')}
                    onChange={e => setSearchVal(e.currentTarget.value)}
                    onClear={() => setSearchVal('')}
                    size="lg"
                    clearable
                    prefix={<IconFont type="SearchLine" />}
                />
                {list.length > 0 ? (
                    <SC.TagList>
                        <SC.TagWrapper>
                            {list.map(item => (
                                <SC.TagItem key={item.id} onClick={() => handleRoleChange(item.id)}>
                                    {tagComponent ? tagComponent({ data: item }) : <SC.TagItemContent>{item.name}</SC.TagItemContent>}
                                    {roleValue.includes(item.id) && <SC.Icon type="Tick" />}
                                </SC.TagItem>
                            ))}
                        </SC.TagWrapper>
                    </SC.TagList>
                ) : (
                    <Empty
                        styles={{
                            root: {
                                padding: '42px'
                            }
                        }}
                        icon="SearchLine"
                        description={convertTextByLanguage('noFindData')}
                    />
                )}
            </SC.Container>
        </MobileModal>
    )
}
